import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Pagination } from 'components/Pagination/Pagination'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { clearFuelDrums, getFuelDrums } from 'features/fuelManagement/fuelCustodySlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { mapFuelDrum } from 'pages/FuelManagement/Shared/fuelApiMappers'
import { FUEL_DRUM_FLOAT_SUMMARY_SUFFIX } from 'pages/FuelManagement/Shared/fuelFormatters'
import { RootState } from 'store'
import { Flex } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import { IFuelDrumFloat } from 'types/fuelOps.interface'
import { formatNumbersForDisplay, getParams, handlePagination } from 'utils/helpers'
import {
    DrumCellEm,
    DrumCellNote,
    DrumCellStack,
    DrumCellText,
    DrumFloatValue,
    DrumSummaryLine,
} from './DrumFloatPanel.styled'

const litres = (value: number): string => `${formatNumbersForDisplay(value)} L`

const columns: IHonestTableColumn<IFuelDrumFloat>[] = [
    {
        id: 'ref',
        header: 'REF',
        cell: (item) => (
            <DrumCellStack>
                <DrumCellEm>{item.ref}</DrumCellEm>
                <DrumCellNote>{item.note}</DrumCellNote>
            </DrumCellStack>
        ),
    },
    { id: 'issuedTo', header: 'ISSUED TO', cell: (item) => <DrumCellText>{item.issuedTo}</DrumCellText> },
    {
        id: 'issued',
        header: 'ISSUED',
        align: 'right',
        cell: (item) => (
            <DrumCellStack>
                <DrumCellText>{litres(item.issuedL)}</DrumCellText>
                <DrumCellNote>{item.issuedLabel}</DrumCellNote>
            </DrumCellStack>
        ),
    },
    {
        id: 'confirmedUsed',
        header: 'CONFIRMED USED',
        align: 'right',
        cell: (item) => (
            <DrumCellStack>
                <DrumCellText>{litres(item.confirmedUsedL)}</DrumCellText>
                <DrumCellNote>{item.confirmedVia}</DrumCellNote>
            </DrumCellStack>
        ),
    },
    {
        id: 'returnedEmpty',
        header: 'RETURNED EMPTY',
        align: 'right',
        cell: (item) => <DrumCellText>{litres(item.returnedEmptyL)}</DrumCellText>,
    },
    {
        id: 'float',
        header: 'FLOAT',
        align: 'right',
        cell: (item) => <DrumFloatValue $open={item.status === 'OPEN'}>{litres(item.floatL)}</DrumFloatValue>,
    },
    {
        id: 'status',
        header: 'STATUS',
        cell: (item) => (
            <StatusPill status={item.status === 'OPEN' ? 'warning' : 'success'}>{item.status}</StatusPill>
        ),
    },
]

export const DrumFloatPanel = () => {
    const [params, setParams] = useState<any>(getParams({ location }))
    const { drums } = useSelector((store: RootState) => store.fuelCustody)

    useQueryPagination({
        fetchFunctions: [getFuelDrums],
        cleanUpFunctions: [clearFuelDrums],
        params,
    })

    const rows: IFuelDrumFloat[] = useMemo(() => drums.data.map(mapFuelDrum), [drums.data])
    const openFloatL = rows.filter((r) => r.status === 'OPEN').reduce((sum, r) => sum + r.floatL, 0)

    return (
        <Container>
            <ContainerHeader>
                <Flex center gap='0.75rem'>
                    <TitleText>Drum float</TitleText>
                </Flex>
            </ContainerHeader>
            <ContainerContent gap='0.75rem'>
                <DrumSummaryLine>
                    {`${litres(openFloatL)} open float across ${rows.filter((r) => r.status === 'OPEN').length} drums`}
                    {FUEL_DRUM_FLOAT_SUMMARY_SUFFIX}
                </DrumSummaryLine>
                <HonestTable
                    columns={columns}
                    data={rows}
                    keyField='id'
                    emptyMessage='No drum floats yet — drum-method dispenses create open floats here.'
                />
                {drums.totalPages > 1 && (
                    <Pagination
                        setPage={(page) => handlePagination(page, setParams)}
                        pageCount={drums.totalPages}
                        forcePage={(params.page ? params.page : 1) - 1}
                    />
                )}
            </ContainerContent>
        </Container>
    )
}
